'use client';

import { useEffect } from 'react';

const GOOGLE_TAG_SCRIPT_ATTRIBUTE = 'data-hutchinson-google-tag';

function normalizeValue(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function hasExternalScript(googleTag) {
    return Array.from(
        document.querySelectorAll(`script[${GOOGLE_TAG_SCRIPT_ATTRIBUTE}]`)
    ).some(
        (script) =>
            script.getAttribute(GOOGLE_TAG_SCRIPT_ATTRIBUTE) === googleTag
    );
}

function ensureExternalScript(src, googleTag) {
    if (hasExternalScript(googleTag)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.setAttribute(GOOGLE_TAG_SCRIPT_ATTRIBUTE, googleTag);
    document.head.appendChild(script);
}

function installGtag(googleTag) {
    window.dataLayer = window.dataLayer || [];

    const previousGtag = window.gtag;
    const gtag =
        typeof previousGtag === 'function'
            ? previousGtag
            : function pushToDataLayer() {
                  window.dataLayer.push(arguments);
              };

    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', googleTag);

    return () => {
        if (window.gtag !== gtag) return;
        if (typeof previousGtag === 'function') window.gtag = previousGtag;
        else delete window.gtag;
    };
}

function installGoogleTag(googleTag) {
    ensureExternalScript(
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
            googleTag
        )}`,
        googleTag
    );
    return installGtag(googleTag);
}

function installGoogleTagManager(googleTag) {
    window.dataLayer = window.dataLayer || [];
    if (hasExternalScript(googleTag)) return;

    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
    ensureExternalScript(
        `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
            googleTag
        )}`,
        googleTag
    );
}

export default function GoogleTagScripts({ googleTag }) {
    useEffect(() => {
        const tag = normalizeValue(googleTag);
        if (!tag) return undefined;

        if (
            tag.startsWith('G-') ||
            tag.startsWith('UA-') ||
            tag.startsWith('AW-')
        ) {
            return installGoogleTag(tag);
        }

        if (tag.startsWith('GTM-')) installGoogleTagManager(tag);
        return undefined;
    }, [googleTag]);

    return null;
}
